/**
 * Rudrik Crypto Screening
 * ========================
 * AMLBot-powered crypto wallet & transaction AML risk screening.
 * Supports single-wallet, bulk batch, and transaction hash modes.
 * Falls back to deterministic mock when AMLBOT_API_KEY not configured.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Bitcoin, Loader2, AlertTriangle, ShieldOff, ShieldCheck, Coins,
  Hash, Layers, FileSearch, Wallet, FlaskConical, Sparkles,
  Cpu, Award, FileText, MapPin, Activity,
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/crypto-screen`;

const CURRENCIES = [
  "BTC", "ETH", "USDT_ERC20", "USDT_TRC20", "BNB",
  "SOL", "LTC", "BCH", "MATIC", "DOGE",
];

const RISK_COLOR = { Low: "#22c55e", Medium: "#f59e0b", High: "#dc2626" };
const ACTION_COLOR = {
  ALLOW: "#22c55e", STEP_UP_KYC: "#eab308",
  MANUAL_REVIEW: "#f97316", BLOCK: "#dc2626",
};

const SIGNAL_META = {
  sanctions: { label: "Sanctions", icon: ShieldOff, color: "#dc2626" },
  darknet: { label: "Darknet", icon: ShieldOff, color: "#a855f7" },
  mixer: { label: "Mixer", icon: Layers, color: "#f97316" },
  gambling: { label: "Gambling", icon: Coins, color: "#eab308" },
  scam: { label: "Scam", icon: AlertTriangle, color: "#fb7185" },
  trusted: { label: "Trusted", icon: ShieldCheck, color: "#22c55e" },
};

function Bar({ value }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div style={{ position: "relative", width: "100%", height: 6, background: "#1e2530", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0,
                    width: `${v}%`,
                    background: v >= 60 ? "#dc2626" : v >= 30 ? "#f59e0b" : "#22c55e",
                    transition: "width 220ms ease" }} />
    </div>
  );
}

function ResultCard({ r }) {
  const riskColor = RISK_COLOR[r?.risk] || "#94a3b8";
  const actionColor = ACTION_COLOR[r?.recommended_action] || "#94a3b8";
  return (
    <div data-testid={`crypto-result-${r.address || r.hash || "x"}`}
         style={{ background: "#0d1117", border: `1px solid ${riskColor}55`,
                  borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 54, height: 54, borderRadius: "50%",
          background: `conic-gradient(${riskColor} ${r.score * 3.6}deg, #1e2530 0)`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#0d1117",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: riskColor, fontSize: 16, fontWeight: 800 }}>
            {r.score}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: "#cbd5e1",
                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.address || r.hash}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            <span style={{ color: riskColor, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {r.risk}
            </span>
            <span style={{ color: "#64748b", fontSize: 11 }}>· {r.currency}</span>
            {r.source && (
              <span style={{ marginLeft: "auto", color: r.source === "amlbot_live" ? "#22c55e" : "#f59e0b",
                              fontSize: 8, fontWeight: 800,
                              padding: "2px 6px", borderRadius: 4,
                              background: r.source === "amlbot_live" ? "rgba(34,197,94,0.10)" : "rgba(245,158,11,0.10)",
                              border: `1px solid ${r.source === "amlbot_live" ? "#22c55e55" : "#f59e0b55"}` }}>
                {r.source === "amlbot_live" ? "LIVE · AMLBOT" : "MOCK"}
              </span>
            )}
          </div>
        </div>
      </div>

      {r.entity && (
        <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid #dc262655",
                      borderRadius: 6, padding: "6px 10px", marginBottom: 8,
                      display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
          <Sparkles size={11} style={{ color: "#fca5a5" }} />
          <span style={{ color: "#fca5a5", fontWeight: 700 }}>{r.entity.name}</span>
          <span style={{ color: "#94a3b8" }}>· {r.entity.type}</span>
          <span style={{ marginLeft: "auto", color: r.entity.status === "Sanctioned" ? "#fca5a5" : "#cbd5e1", fontWeight: 700 }}>
            {r.entity.status}
          </span>
        </div>
      )}

      {r.signals && (
        <div style={{ display: "grid", gap: 5, marginBottom: 8 }}>
          {Object.entries(r.signals).map(([key, val]) => {
            const meta = SIGNAL_META[key] || { label: key, color: "#94a3b8" };
            const Icon = meta.icon;
            return (
              <div key={key} style={{ display: "grid", gridTemplateColumns: "16px 90px 1fr 36px", gap: 6, alignItems: "center" }}>
                {Icon ? <Icon size={11} style={{ color: meta.color }} /> : <span />}
                <span style={{ fontSize: 10, color: "#94a3b8" }}>{meta.label}</span>
                <Bar value={val} />
                <span style={{ fontSize: 10, color: "#cbd5e1", textAlign: "right" }}>{val}%</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{
        background: `${actionColor}10`, border: `1px solid ${actionColor}55`,
        borderRadius: 6, padding: "8px 10px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle size={11} style={{ color: actionColor }} />
          <span style={{ color: actionColor, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {(r.recommended_action || "").replace(/_/g, " ")}
          </span>
          {r.str_required && (
            <span style={{ marginLeft: "auto", color: "#fca5a5", fontSize: 9, fontWeight: 800 }}>
              STR · 7 days
            </span>
          )}
        </div>
        <div style={{ color: "#cbd5e1", fontSize: 11, marginTop: 4 }}>{r.recommendation_reason}</div>
      </div>
    </div>
  );
}

const DECISION_COLOR = {
  APPROVE: "#22c55e", MONITOR: "#3b82f6",
  EDD: "#f59e0b", REJECT: "#dc2626", BLOCK_FILE_STR: "#dc2626",
};
const RISK_LEVEL_COLOR = {
  LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#f97316", CRITICAL: "#dc2626",
};

function GodEngineResult({ r }) {
  const decisionColor = DECISION_COLOR[r.final_decision] || "#94a3b8";
  const levelColor = RISK_LEVEL_COLOR[r.risk_level] || "#94a3b8";
  return (
    <div data-testid="god-engine-result" style={{ display: "grid", gap: 12 }}>
      {/* Hero verdict card */}
      <div style={{
        background: "#0d1117", border: `2px solid ${decisionColor}77`, borderRadius: 12, padding: 20,
        display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 18, alignItems: "center",
      }}>
        <div style={{
          width: 76, height: 76, borderRadius: "50%",
          background: `conic-gradient(${levelColor} ${r.risk_score * 3.6}deg, #1e2530 0)`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{ width: 62, height: 62, borderRadius: "50%", background: "#0d1117",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div style={{ color: levelColor, fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{r.risk_score}</div>
            <div style={{ color: "#64748b", fontSize: 9, marginTop: 2 }}>/100</div>
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ color: levelColor, fontSize: 18, fontWeight: 800 }} data-testid="god-risk-display">{r.risk_display}</span>
            <span style={{ marginLeft: 8, color: "#94a3b8", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{r.screening_id}</span>
            <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 10 }}>{r.ai_mode} · {r.latency_ms}ms</span>
          </div>
          <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.5 }}>{r.summary}</div>
          <div style={{ color: "#64748b", fontSize: 10, marginTop: 6, fontFamily: "ui-monospace, monospace",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.wallet} · {r.chain}
          </div>
        </div>
        <div style={{ textAlign: "center", minWidth: 130 }}>
          <div style={{ color: "#94a3b8", fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6 }}>Final Decision</div>
          <div data-testid="god-decision" style={{ color: decisionColor, fontSize: 18, fontWeight: 800, margin: "4px 0", letterSpacing: 0.4 }}>
            {r.final_decision.replace(/_/g, " ")}
          </div>
          <div style={{ color: "#cbd5e1", fontSize: 10 }}>CDD: <span style={{ color: levelColor, fontWeight: 700 }}>{r.cdd_tier}</span></div>
        </div>
      </div>

      {/* Sanctions / wallet intel grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <section data-testid="god-sanctions" style={{ background: "#0d1117", border: `1px solid ${r.sanctions.ofac_direct ? "#dc262677" : "#1e2530"}`,
                                                       borderRadius: 10, padding: 12 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <ShieldOff size={11} style={{ color: "#dc2626" }} /> Sanctions Check
          </div>
          {[
            ["OFAC direct hit", r.sanctions.ofac_direct],
            ["OpenSanctions match", r.sanctions.opensanctions_match],
            ["India ED-linked", r.sanctions.india_ed_linked],
            ["DPRK-linked (Lazarus)", r.sanctions.dprk_linked],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, margin: "3px 0" }}>
              <span style={{ color: "#94a3b8" }}>{k}</span>
              <span style={{ color: v ? "#fca5a5" : "#22c55e", fontWeight: 700 }}>{v ? "YES" : "no"}</span>
            </div>
          ))}
          {r.sanctions.sanctioned_entity && (
            <div style={{ marginTop: 6, padding: "5px 9px", background: "rgba(220,38,38,0.10)",
                          border: "1px solid #dc262655", borderRadius: 4, fontSize: 11, color: "#fca5a5" }}>
              <strong>Entity:</strong> {r.sanctions.sanctioned_entity}
            </div>
          )}
        </section>

        <section data-testid="god-wallet-intel" style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 12 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Activity size={11} style={{ color: "#60a5fa" }} /> Wallet Intelligence
            <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 8, fontWeight: 700, textTransform: "uppercase" }}>
              {r.wallet_intelligence.source}
            </span>
          </div>
          {[
            ["Age", `${r.wallet_intelligence.age_days} days${r.wallet_intelligence.is_new ? " · NEW" : ""}${r.wallet_intelligence.is_dormant ? " · DORMANT" : ""}`],
            ["Balance", r.wallet_intelligence.total_value_usd],
            ["Transactions", r.wallet_intelligence.tx_count],
            ["Counterparties", r.wallet_intelligence.counterparties],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, margin: "3px 0" }}>
              <span style={{ color: "#94a3b8" }}>{k}</span>
              <span style={{ color: "#cbd5e1", fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </section>
      </div>

      {/* Risk categories grid */}
      <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 12 }}>
        <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
          Risk Categories
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
          {Object.entries(r.risk_categories).map(([key, val]) => {
            const c = val.score >= 50 ? "#dc2626" : val.score >= 20 ? "#f59e0b" : val.score >= 5 ? "#eab308" : "#22c55e";
            return (
              <div key={key} title={val.detail} style={{
                background: "#080c12", border: `1px solid ${c}33`, borderRadius: 6, padding: "8px 6px", textAlign: "center",
              }}>
                <div style={{ color: c, fontSize: 18, fontWeight: 800 }}>{val.score}%</div>
                <div style={{ color: "#94a3b8", fontSize: 9, marginTop: 3, textTransform: "uppercase", letterSpacing: 0.5 }}>{key}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* India compliance + STR filing */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <section data-testid="god-india-compliance" style={{
          background: "#0d1117",
          border: `1px solid ${r.india_compliance.fiu_ind_str_needed ? "#dc262677" : "#1e2530"}`,
          borderRadius: 10, padding: 12,
        }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <MapPin size={11} style={{ color: "#22c55e" }} /> India PMLA Compliance
          </div>
          {[
            ["PMLA 2002 applicable", r.india_compliance.pmla_2002_applicable],
            ["VASP reporting needed", r.india_compliance.vasp_reporting_needed],
            ["FIU-IND STR needed", r.india_compliance.fiu_ind_str_needed],
            ["Foreign-exchange concern", r.india_compliance.foreign_exchange_concern],
            ["ED referral needed", r.india_compliance.ed_referral_needed],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, margin: "3px 0" }}>
              <span style={{ color: "#94a3b8" }}>{k}</span>
              <span style={{ color: v ? "#fca5a5" : "#22c55e", fontWeight: 700 }}>{v ? "YES" : "no"}</span>
            </div>
          ))}
        </section>

        <section data-testid="god-str" style={{
          background: "#0d1117",
          border: `1px solid ${r.str_filing.required ? "#dc262677" : "#1e2530"}`,
          borderRadius: 10, padding: 12,
        }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <FileText size={11} style={{ color: "#fca5a5" }} /> STR Filing
          </div>
          {r.str_filing.required ? (
            <>
              <div style={{ color: "#fca5a5", fontSize: 16, fontWeight: 800 }}>
                Required · {r.str_filing.urgency === "IMMEDIATE" ? "24h" : "7 days"}
              </div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>Type: {r.str_filing.str_type}</div>
              <div style={{ color: "#cbd5e1", fontSize: 11, marginTop: 6 }}>{r.str_filing.reason}</div>
            </>
          ) : (
            <div style={{ color: "#22c55e", fontSize: 14, fontWeight: 700 }}>Not required ✓</div>
          )}
        </section>
      </div>

      {/* Risk flags */}
      {r.risk_flags?.length > 0 && (
        <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 12 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
            Risk Flags ({r.risk_flags.length})
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {r.risk_flags.map((f, i) => {
              const c = { CRITICAL: "#dc2626", HIGH: "#f97316", MEDIUM: "#f59e0b", LOW: "#22c55e" }[f.severity] || "#94a3b8";
              return (
                <div key={i} data-testid={`god-flag-${i}`} style={{
                  background: `${c}10`, border: `1px solid ${c}55`, borderRadius: 6, padding: "8px 10px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <AlertTriangle size={11} style={{ color: c }} />
                    <span style={{ color: c, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>{f.severity}</span>
                    <span style={{ color: "#f1f5f9", fontSize: 11, fontWeight: 700 }}>{f.flag}</span>
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 10, marginLeft: 17 }}>{f.evidence}</div>
                  <div style={{ color: "#cbd5e1", fontSize: 10, marginLeft: 17, marginTop: 3 }}>→ {f.action}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Recommended actions + documents */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 10 }}>
        <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 12 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
            Recommended Actions
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {r.recommended_actions?.map((a, i) => (
              <div key={i} data-testid={`god-action-${i}`} style={{
                background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 10px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <span style={{
                    background: "#1e2530", color: "#cbd5e1", borderRadius: "50%",
                    width: 18, height: 18, fontSize: 9, fontWeight: 800,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>{a.priority}</span>
                  <span style={{ color: "#f1f5f9", fontSize: 11, fontWeight: 600 }}>{a.action}</span>
                </div>
                <div style={{ color: "#64748b", fontSize: 9, marginLeft: 24 }}>
                  Owner: {a.owner} · Deadline: {new Date(a.deadline).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 12 }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
            Documents to Collect
          </div>
          {r.documents_to_collect?.length > 0 ? (
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {r.documents_to_collect.map((d, i) => (
                <li key={i} style={{ color: "#cbd5e1", fontSize: 11, padding: "5px 0",
                                      borderBottom: i < r.documents_to_collect.length - 1 ? "1px solid #1e2530" : "none" }}>
                  <ShieldCheck size={10} style={{ color: "#22c55e", marginRight: 6, verticalAlign: -1 }} />
                  {d}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ color: "#fca5a5", fontSize: 11 }}>None — wallet blocked, no onboarding.</div>
          )}
        </section>
      </div>

      {/* Certificate */}
      <section data-testid="god-certificate" style={{
        background: "linear-gradient(135deg, #0d1117, #131a23)",
        border: `2px solid ${decisionColor}55`, borderRadius: 12, padding: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Award size={16} style={{ color: decisionColor }} />
          <span style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 800, letterSpacing: 0.4 }}>Rudrik Crypto AML Certificate</span>
          <span style={{ marginLeft: "auto", color: "#94a3b8", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>
            {r.certificate.number}
          </span>
        </div>
        <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.5 }}>{r.certificate.statement}</div>
        <div style={{ color: "#64748b", fontSize: 9, marginTop: 8 }}>
          Generated: {new Date(r.screened_at).toLocaleString()} · Session-only · Equivalent to Chainalysis/TRM Labs verdict
        </div>
      </section>
    </div>
  );
}

export default function CryptoScreenPage() {
  const [mode, setMode] = useState("god"); // god | wallet | transaction | bulk
  const [currency, setCurrency] = useState("BTC");
  const [address, setAddress] = useState("");
  const [txHash, setTxHash] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [godResult, setGodResult] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);
  const [status, setStatus] = useState(null);
  const [samples, setSamples] = useState({ wallets: [], transactions: [] });

  useEffect(() => {
    axios.get(`${API}/status`, { withCredentials: true })
      .then(({ data }) => setStatus(data)).catch(() => {});
    axios.get(`${API}/samples`, { withCredentials: true })
      .then(({ data }) => setSamples(data)).catch(() => {});
  }, []);

  const bulkSummary = useMemo(() => bulkResults?.summary, [bulkResults]);

  const screen = async () => {
    setLoading(true); setResult(null); setGodResult(null); setBulkResults(null);
    try {
      if (mode === "god") {
        if (!address.trim()) { toast.error("Enter a wallet address"); return; }
        const { data } = await axios.post(`${API}/god-engine`,
          { address: address.trim(), chain: currency }, { withCredentials: true, timeout: 25000 });
        setGodResult(data.result);
        const r = data.result;
        const t = `${r.risk_display} · ${r.final_decision.replace(/_/g, " ")}`;
        if (r.risk_level === "CRITICAL") toast.error(t);
        else if (r.risk_level === "HIGH") toast.warning(t);
        else toast.success(t);
      } else if (mode === "wallet") {
        if (!address.trim()) { toast.error("Enter a wallet address"); return; }
        const { data } = await axios.post(`${API}/wallet`,
          { address: address.trim(), currency }, { withCredentials: true });
        setResult(data.result);
        toast(`${data.result?.risk || "?"} risk · score ${data.result?.score ?? "?"}`);
      } else if (mode === "transaction") {
        if (!txHash.trim()) { toast.error("Enter a transaction hash"); return; }
        const { data } = await axios.post(`${API}/transaction`,
          { hash: txHash.trim(), currency }, { withCredentials: true });
        setResult(data.result);
        toast(`${data.result?.risk || "?"} risk · score ${data.result?.score ?? "?"}`);
      } else {
        const addrs = bulkText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        if (addrs.length === 0) { toast.error("Paste at least one address"); return; }
        if (addrs.length > 100) { toast.error("Max 100 addresses per batch"); return; }
        const { data } = await axios.post(`${API}/bulk`,
          { addresses: addrs, currency }, { withCredentials: true });
        setBulkResults(data);
        const s = data.summary || {};
        toast(`Screened ${s.count} · H:${s.high} M:${s.medium} L:${s.low}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Screening failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="crypto-screen-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-1">
        <Bitcoin style={{ width: 26, height: 26, color: "#f7931a" }} />
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Crypto Screening</h1>
        {status && (
          <span data-testid="provider-status" style={{
            background: status.configured ? "rgba(34,197,94,0.12)" : "rgba(245,158,11,0.12)",
            color: status.configured ? "#22c55e" : "#f59e0b",
            border: `1px solid ${status.configured ? "#22c55e55" : "#f59e0b55"}`,
            fontSize: 10, padding: "3px 8px", borderRadius: 4, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.5,
          }}>
            {status.configured ? "LIVE · AMLBot" : "Mock fallback"}
          </span>
        )}
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
        Screen crypto wallets and transactions against global AML risk databases (sanctions, darknet,
        mixers, gambling, scam exposure). Returns 0–100 risk score with recommended compliance action.
        {status && !status.configured && (
          <span style={{ color: "#f59e0b", marginLeft: 6 }}>
            Add <code style={{ color: "#fbbf24" }}>AMLBOT_API_KEY</code> to <code>backend/.env</code> for live data (signup: <a href="https://amlbot.com" target="_blank" rel="noreferrer" style={{ color: "#f59e0b" }}>amlbot.com</a> · Pro $9 for 20 checks).
          </span>
        )}
      </p>

      {/* Mode tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          { id: "god", icon: Cpu, label: "God Engine" },
          { id: "wallet", icon: Wallet, label: "Single Wallet" },
          { id: "transaction", icon: Hash, label: "Transaction Hash" },
          { id: "bulk", icon: Layers, label: "Bulk Wallets" },
        ].map((m) => (
          <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => { setMode(m.id); setResult(null); setGodResult(null); setBulkResults(null); }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: mode === m.id
                ? (m.id === "god" ? "linear-gradient(135deg, #a855f7, #7c3aed)" : "linear-gradient(135deg, #f7931a, #c2410c)")
                : "#0d1117",
              border: mode === m.id
                ? (m.id === "god" ? "1px solid #a855f7" : "1px solid #f7931a")
                : "1px solid #1e2530",
              color: mode === m.id ? "white" : "#94a3b8",
              borderRadius: 8, padding: "8px 14px",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
            <m.icon size={13} />
            {m.label}
            {m.id === "god" && (
              <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3,
                              background: mode === m.id ? "rgba(255,255,255,0.18)" : "rgba(168,85,247,0.18)",
                              color: mode === m.id ? "white" : "#c4b5fd" }}>PMLA</span>
            )}
          </button>
        ))}
      </div>

      {/* Form */}
      <section style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 8, marginBottom: 8 }}>
          {(mode === "wallet" || mode === "god") && (
            <input value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="Wallet address e.g. bc1q... or 0x..."
              data-testid="address-input"
              style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6,
                       padding: 10, color: "#f1f5f9", fontSize: 12, fontFamily: "ui-monospace, monospace" }} />
          )}
          {mode === "transaction" && (
            <input value={txHash} onChange={(e) => setTxHash(e.target.value)}
              placeholder="Transaction hash e.g. abc123def..."
              data-testid="tx-input"
              style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6,
                       padding: 10, color: "#f1f5f9", fontSize: 12, fontFamily: "ui-monospace, monospace" }} />
          )}
          {mode === "bulk" && (
            <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)}
              placeholder="One address per line (max 100)"
              data-testid="bulk-input"
              style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6,
                       padding: 10, color: "#f1f5f9", fontSize: 12, fontFamily: "ui-monospace, monospace",
                       minHeight: 120, resize: "vertical" }} />
          )}
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="currency-select"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 6,
                     padding: 10, color: "#f1f5f9", fontSize: 12, fontWeight: 700 }}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
        </div>
        <button onClick={screen} disabled={loading} data-testid="screen-btn"
          style={{
            width: "100%",
            background: loading ? "#1e2530"
              : (mode === "god" ? "linear-gradient(135deg, #a855f7, #7c3aed)" : "linear-gradient(135deg, #f7931a, #c2410c)"),
            border: "none", color: "white", borderRadius: 6, padding: "10px 18px",
            fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
          {loading ? <Loader2 className="animate-spin" size={14} /> : (mode === "god" ? <Cpu size={14} /> : <FileSearch size={14} />)}
          {loading
            ? (mode === "god" ? "Running God Engine…" : "Screening…")
            : (mode === "god"
                ? "Run God Engine (AMLBot + OFAC + OpenSanctions + PMLA)"
                : `Screen ${mode === "transaction" ? "transaction" : mode === "bulk" ? "batch" : "wallet"}`)}
        </button>
      </section>

      {/* Sample chips */}
      {(mode === "wallet" || mode === "god") && samples.wallets?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          <span style={{ color: "#64748b", fontSize: 11, alignSelf: "center", marginRight: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <FlaskConical size={11} /> Demo wallets:
          </span>
          {samples.wallets.map((s) => (
            <button key={s.address} onClick={() => { setAddress(s.address); setCurrency(s.currency); }}
              data-testid={`sample-wallet-${s.currency}`}
              style={{ background: "#0d1117", border: "1px solid #1e2530", color: "#cbd5e1",
                       borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* God Engine result */}
      {godResult && <GodEngineResult r={godResult} />}

      {/* Single result */}
      {result && <ResultCard r={result} />}

      {/* Bulk results */}
      {bulkResults && (
        <>
          {bulkSummary && (
            <div data-testid="bulk-summary" style={{
              display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10,
              background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 12,
            }}>
              {[
                { label: "Screened", value: bulkSummary.count, color: "#60a5fa" },
                { label: "High risk", value: bulkSummary.high, color: "#dc2626" },
                { label: "Medium", value: bulkSummary.medium, color: "#f59e0b" },
                { label: "Low / clean", value: bulkSummary.low, color: "#22c55e" },
              ].map((s) => (
                <div key={s.label} style={{ textAlign: "center" }}>
                  <div style={{ color: "#94a3b8", fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6 }}>{s.label}</div>
                  <div style={{ color: s.color, fontSize: 28, fontWeight: 800 }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {bulkResults.results.map((r) => <ResultCard key={r.address || r.hash} r={r} />)}
          </div>
        </>
      )}
    </div>
  );
}
